/**
 * The application shell (ORDR-30, moved onto pack composition in UIFR-1628).
 *
 * One PWA, five modes. Everything visible here comes from the framework:
 * `PwaShell` (UIFR-1615/1571) wraps installability and the offline banner,
 * `ChameleonShell` (UIFR-1616) resolves which of customer / kitchen / staff /
 * admin / onboarding this acting node permits and lazily loads only that route.
 *
 * WHAT THIS FILE OWNS is the seam, and only the seam: which node the user is
 * acting for, WHICH URL IS OPEN, and what happens when the resolver returns a
 * flow the client must refuse. It renders no mode surface.
 *
 * THE MODE IS NOW A URL. `ShellProvider` mounts `ORDERING_PACK`, whose route
 * table is where `/kitchen` becomes a path, and `ChameleonShell` is driven from
 * it as a CONTROLLED component. Before this, the mode was state inside the
 * framework and a kitchen display that reloaded came back as an order screen.
 *
 * ORDR-118 adds what the diner sees around the menu and where the sibling
 * surfaces are mounted:
 * - the LOCATION CHOOSER, on the customer surface when the address names a
 *   brand and no Site (it is not a route: choosing is a document load);
 * - the STOREFRONT notice above the menu, never instead of it, and the order's
 *   quoted ready time once an order exists;
 * - the CONFIGURATION SURFACE in the `/admin` panel and the DAILY CONTROLS in a
 *   new kitchen panel, both beside the shell rather than inside its mode gate
 *   (the ORDR-101 trap), scoped to the acting node.
 * `/order/history` stays unmounted (ruling B8; see `reservedPaths.ts`).
 *
 * ORDR-166 makes the diner's whole path the app's own: Menu, Item, Cart,
 * Checkout and Status (`../order/OrderScreens.tsx`, `../status/`). The
 * framework's `CustomerOrdering` is no longer mounted for a diner, and the card
 * form and its tokeniser exist only after commerce refuses pay in store.
 *
 * ORDR-164 puts the SPLASH in front of two of those: the brand's message before
 * the chooser, and the chosen location's before the menu, each once per visit
 * (`../splash/SplashGate.tsx`). It never replaces either; it only goes first.
 *
 * ORDR-194 teaches it the RESELLER node (`node_type`, ORDR-193). A reseller
 * has no brand, location or site here, so while one is acting the shell offers
 * only onboarding, still on `merchant:onboard`, the build switch and a staff
 * host, beside a pointer to the business switcher. Every other mode, and every
 * panel beside them, is withheld whatever capabilities the node carries.
 * ORDR-203: the reseller gets the onboarding PANEL alone, never `ChameleonShell`,
 * whose onboarding route is a location's and warned a reseller falsely.
 *
 * ORDR-204 makes that one-way: "Onboard restaurant" is offered ONLY at a
 * RESELLER node, and only once the session has an active business. paio-auth's
 * platform-admin bridge grants `merchant:onboard` at every resource by design,
 * so a platform admin acting at a restaurant was offered it in production and
 * created a setup with the restaurant as its reseller. A BRAND, LOCATION or SITE
 * node, or one whose type was never sent, has `merchant:onboard` taken out of
 * what the shell gates on, so no mode bar draws Onboarding there.
 *
 * ORDR-209: the same gate draws "Onboard business" beside the business
 * switcher, through the slot the staff door leaves there
 * (`../session/businessActions.ts`). It opens `/onboarding` on the form, and
 * there is no second gate to drift from this one.
 *
 * ORDR-226: the ORDER LINK. On the customer surface an `orderLink` (a valid
 * `?order=` with no Site) mounts the Status page alone, ahead of the chooser,
 * the storefront notice and the splash: the diner has already ordered, and
 * nothing else on the address is needed to read the order.
 *
 * THE GATE IS COSMETIC. The pack's capabilities decide what is drawn; the
 * server enforces the same set independently on every request and would reject
 * a caller who reached a mode by typing its path.
 */

import {
  Suspense,
  lazy,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { GRANT_CAPABILITY } from "../access/capabilities.js";
import { GrantsPanel } from "../access/GrantsPanel.js";
import { ConfigSurface, DailyControls, type ConfigScope } from "../admin/config/index.js";
import { BrandedSurface } from "../brand/BrandedSurface.js";
import { LocationChooser } from "../locations/index.js";
import { MenuDisclosure } from "../menu/index.js";
import type { CardPayment, PlacedOrder } from "../checkout/CheckoutScreen.js";
import { OrderScreens } from "../order/index.js";
import { SplashGate } from "../splash/SplashGate.js";
import { rememberOrder } from "../status/orderMemory.js";
import { StorefrontStatus } from "../storefront/StorefrontStatus.js";
import type { FinixCardDeps } from "@pirouz-ca/payments-core";
import {
  ChameleonShell,
  PwaShell,
  type ActingNode,
  type FlowFault,
  type ResolvedFlow,
  type ShellMode,
  type ShellRouteProps,
  useBrand,
} from "@pirouz-ca/ui-components";
import { ShellProvider, useShell } from "@pirouz-ca/ui-dashboard-shell";
import type { ApiClient } from "../api/client.js";
import { ONBOARDING_ENABLED, STAFF_HOSTS, isStaffHost, type FinixCardPlatform } from "../config/env.js";
import { createCardTokeniser, type CardCaptureSlot } from "../customer/cardTokeniser.js";
import type { CartSecretStore } from "../customer/cartSecretStore.js";
import { createCommerceClient } from "../customer/commerceClient.js";
import { CustomerStatus } from "../customer/CustomerStatus.js";
import type { PaymentTokeniser } from "../customer/customerServices.js";
import { useCustomerOrdering } from "../customer/useCustomerOrdering.js";
import type { DeviceStore } from "../kitchen/deviceStore.js";
import { KitchenStatus } from "../kitchen/KitchenStatus.js";
import { useKitchenStation } from "../kitchen/useKitchenStation.js";
import { BusinessActionsSlot } from "../session/businessActions.js";
import type { ContextNodeType } from "../session/contextNodes.js";
import { gateFlow } from "./flowGate.js";
import {
  ORDERING_BASE_PATH,
  ORDERING_DEFAULT_TITLE,
  ORDERING_MODE_CAPABILITIES,
  ORDERING_PACK,
  ORDERING_PACKS,
  capabilityCheckFor,
  modeFromPath,
  pathForMode,
  resolveOrderingLabel,
} from "./orderingPack.js";
import { ResellerPointer } from "./ResellerPointer.js";

/**
 * Which door the visitor came in by (ORDR-41).
 *
 * `customer`: a diner, anonymous, with the Site in the address. They hold no
 * node and need none; the customer mode is ungated.
 *
 * `staff`: everybody else. Their nodes come from the ordering context, and an
 * empty list means "not set up to order".
 */
export type OrderingSurface = "customer" | "staff";

export interface OrderingShellProps {
  /** Every node the signed-in user holds, from the ordering context. */
  nodes: readonly ActingNode[];
  /** Defaults to `staff`, where an empty `nodes` renders the notice. */
  surface?: OrderingSurface;
  /**
   * The signed-in user, when there is one (ORDR-94).
   *
   * Present only on the staff surface. It selects what the grants panel may
   * OFFER, from the caller's own grants; it is never an authorization input.
   * The server re-derives the caller from the session cookie on every write.
   */
  ownUserId?: string;
  /** Which node to start acting for. Defaults to the first. */
  initialActingNodeId?: string;
  /** The flow the resolver returned for this session, if one is in progress. */
  flow?: ResolvedFlow;
  /** Called with the diner's answers once they confirm at the review step. */
  onFlowSubmit?: (answers: Record<string, string>) => void;
  /** Notified when the client refuses a flow. Wire to telemetry. */
  onFlowFault?: (fault: FlowFault, flow: ResolvedFlow) => void;
  /** The Site each staff node trades at (ORDR-42). The kitchen polls it. */
  siteIdByNode?: Readonly<Record<string, string>>;
  /**
   * Each staff node's `node_type` (ORDR-194). A RESELLER node is offered only
   * onboarding; a node absent from it is treated exactly as before.
   */
  nodeTypeByNode?: Readonly<Record<string, ContextNodeType>>;
  /** What the kitchen board needs. Absent, the kitchen route shows its fallback. */
  kitchen?: KitchenWiring;
  /** What the diner's path needs. Absent, the customer route shows its fallback. */
  customer?: CustomerWiring;
  /**
   * The app's one HTTP seam, for the surfaces this shell mounts from sibling
   * modules: the chooser, the configuration surface and the daily controls.
   * Absent, none of them renders.
   */
  client?: ApiClient;
  /**
   * The customer address named a brand and no Site (ORDR-118): render the
   * location chooser instead of the ordering surface.
   */
  chooser?: ChooserWiring;
  /**
   * A valid `?order=` on an address that names no Site (ORDR-226): render that
   * order's Status page, before the chooser or any notice.
   */
  orderLink?: OrderLinkWiring;
  /**
   * The app's own hosts (ORDR-190). "Onboard restaurant" renders on these only:
   * `/onboarding` on a brand host still opens the staff surface, and a brand
   * host is a diner's. Defaults to `VITE_STAFF_HOSTS`.
   */
  staffHosts?: readonly string[];
  /**
   * Whether the session acts for a business (ORDR-204). Without one,
   * paio-onboarding answers 403 "no acting firm", so "Onboard restaurant" waits
   * for a choice in the business switcher. Defaults to `true` for a shell
   * mounted without the staff door; `App` always passes the door's answer.
   */
  businessChosen?: boolean;
  /**
   * The `VITE_ONBOARDING_ENABLED` build switch (ORDR-190). Off, "Onboard
   * restaurant" is not mounted and `/onboarding` renders what it rendered
   * before the flow existed. Defaults to the build's value.
   */
  onboardingEnabled?: boolean;
}

export interface ChooserWiring {
  /** The `?brand=` node, validated and lowercased by `session/address.ts`. */
  readonly brandNodeId: string;
  /** Performs the document load to the chosen location. */
  readonly onChoose: (siteId: string, locationNodeId: string) => void;
}

/**
 * What the Status page needs when no Site is named (ORDR-226). The order id is
 * the whole key of both public reads; the cart secrets are only what
 * `createCommerceClient` is built with, and the order read never sends one.
 */
export interface OrderLinkWiring {
  readonly client: ApiClient;
  readonly secrets: CartSecretStore;
  /** `?order=`, validated and lowercased by `status/orderMemory.ts`. */
  readonly orderId: string;
}

export interface KitchenWiring {
  readonly client: ApiClient;
  readonly deviceStore: DeviceStore;
}

export interface CustomerWiring {
  readonly client: ApiClient;
  readonly siteId: string;
  readonly entry?: string;
  /** `?brand=` after the chooser; sent to the resolver and used for "Change location". */
  readonly brandNodeId?: string;
  /** `?location=` after the chooser; sent to the resolver. */
  readonly locationNodeId?: string;
  readonly secrets: CartSecretStore;
  readonly currency: string;
  /** Test seam: the card tokeniser, handed out only after a pay-in-store refusal. */
  readonly tokeniser?: PaymentTokeniser;
  /** The hosted card form (ORDR-55). Absent when the VITE_FINIX_* platform env is incomplete. */
  readonly card?: CardWiring;
  /** `?order=` from the address (ORDR-166): open the Status page for it. */
  readonly orderId?: string;
  /**
   * A document load, after signing out of a session commerce refused
   * (ORDR-219). Defaults to `window.location.assign`.
   */
  readonly navigate?: (href: string) => void;
}

export interface CardWiring {
  /**
   * Environment and application id, from build env. The merchant is NOT here:
   * it comes from the server per location (ORDR-57).
   */
  readonly platform: FinixCardPlatform;
  /** The form fills it; the tokeniser reads it. */
  readonly slot: CardCaptureSlot;
  /** Test seam: replaces the hosted finix.js loader. */
  readonly deps?: FinixCardDeps;
}

/**
 * LAZY so payments-core lands in the diner's chunk, never in the entry a
 * kitchen display downloads. `bundleSplit.test.ts` asserts it on built output.
 */
const CardFields = lazy(() => import("../customer/CardFields.js"));

/** LAZY for the same reason: the Status page is reached only after an order. */
const OrderStatusPage = lazy(() => import("../status/OrderStatusPage.js").then((module) => ({ default: module.OrderStatusPage })));

/**
 * LAZY too: "Onboard restaurant" (ORDR-190) is opened by a reseller admin a few
 * times a month and has no business in a kitchen display's entry chunk.
 */
const OnboardingPanel = lazy(() =>
  import("../onboarding/OnboardingPanel.js").then((module) => ({ default: module.OnboardingPanel })),
);

/**
 * What the onboarding panel is gated on: the same capability the pack gates the
 * `/onboarding` mode on, read from the ACTING NODE's resolved list, never from
 * a persona or seat. paio-onboarding and paio-auth resolve their own on every call.
 */
const ONBOARD_CAPABILITY = "merchant:onboard";

const NO_SITES: Readonly<Record<string, string>> = {};
const NO_TYPES: Readonly<Record<string, ContextNodeType>> = {};

interface OutletBase {
  readonly nodes: readonly ActingNode[];
  readonly actingNodeId: string;
  readonly onActingNodeChange: (nodeId: string) => void;
  readonly mode: ShellMode | null;
  readonly onModeChange: (mode: ShellMode) => void;
  readonly onFlowSubmit?: (answers: Record<string, string>) => void;
  readonly onFlowFault?: (fault: FlowFault, flow: ResolvedFlow) => void;
}

interface ModeOutletProps extends OutletBase {
  readonly flow?: ResolvedFlow | undefined;
  readonly routeProps?: ShellRouteProps;
  readonly notice?: ReactNode;
  /**
   * App-supplied content rendered BESIDE the shell, not inside it (ORDR-94,
   * re-gated by ORDR-101).
   *
   * It began as `ChameleonShell`'s children, which put it inside the shell's
   * `RoleGate`: reachable only when the MODE is permitted. Measured in
   * production 2026-09-24, that made the grants panel unreachable by the people
   * who need it. Admin mode gates on `menu:author`, held only by `brand-admin`
   * and `menu-manager`, both BRAND-scoped. `access:grant` is held by
   * `brand-admin`, `location-manager` and `site-operator`. A location-manager
   * therefore holds the authority to grant and could not open the surface,
   * while a menu-manager could open it and holds no authority to use it.
   *
   * So it renders as a sibling, and the caller gates it on the capability it
   * actually needs. Nothing here is a security boundary: the server resolves
   * the same set on every write.
   */
  readonly panel?: ReactNode;
}

/** What the diner is told when the client refuses the resolver's flow. */
function FlowRefused(): ReactElement {
  return (
    <p
      role="alert"
      data-testid="flow-refused"
      className="m-3 rounded-[var(--border-radius-md)] bg-[var(--color-neutral-100)] p-3 text-sm text-[var(--color-neutral-700)]"
    >
      This order cannot be continued. Please ask a member of staff. Nothing has been charged.
    </p>
  );
}

/**
 * The framework outlet, with this app's flow gate in front of it.
 *
 * `routeProps` is how the kitchen's station and callbacks and the customer's
 * services reach the lazily loaded route. `routeProps.test.tsx` proves they
 * arrive behaviourally, because a JSX spread escapes excess-property checks.
 */
function ModeOutlet({
  nodes,
  actingNodeId,
  onActingNodeChange,
  mode,
  onModeChange,
  onFlowSubmit,
  onFlowFault,
  flow,
  routeProps,
  notice,
  panel,
}: ModeOutletProps): ReactElement {
  // Memoised so a re-render does not re-report the same fault to telemetry on
  // every pass. The decision itself is cheap; the reporting is not idempotent.
  const decision = useMemo(
    () => (flow === undefined ? undefined : gateFlow(flow, onFlowFault)),
    [flow, onFlowFault],
  );

  const handleSubmit = useCallback(
    (answers: Record<string, string>) => {
      // Belt and braces. `ChameleonFlow` will not surface a confirm control for
      // a refused flow, so this should be unreachable, which is exactly why it
      // is checked. A submit that arrives for a flow the app refused is not a
      // flow that became valid; it is a bug, and the answer to a bug on the way
      // to payment is to stop.
      if (decision?.proceed !== true) {
        return;
      }
      onFlowSubmit?.(answers);
    },
    [decision, onFlowSubmit],
  );

  return (
    <>
      {decision?.proceed === false && <FlowRefused />}
      {notice}
      <ChameleonShell
        nodes={nodes}
        actingNodeId={actingNodeId}
        onActingNodeChange={onActingNodeChange}
        // REQUIRED HERE, OPTIONAL IN THE FRAMEWORK. Omitted, ChameleonShell
        // falls back to its deprecated default map, which paio-auth never
        // issues, and ungates nothing. modeCapabilityMap.test.tsx fails if
        // this line is removed.
        modeCapabilities={ORDERING_MODE_CAPABILITIES}
        {...(mode === null ? {} : { mode })}
        onModeChange={onModeChange}
        {...(decision?.proceed === true && flow !== undefined ? { flow } : {})}
        onFlowSubmit={handleSubmit}
        {...(routeProps === undefined ? {} : { routeProps })}
      />
      {panel}
    </>
  );
}

function KitchenOutlet({
  wiring,
  siteId,
  panel,
  ...base
}: OutletBase & { wiring: KitchenWiring; siteId: string | null; panel?: ReactNode }): ReactElement {
  const station = useKitchenStation({ client: wiring.client, siteId, deviceStore: wiring.deviceStore });
  // A revoked or unenrolled display shows its notice and NOT the last board it
  // heard: those tickets are no longer this display's to act on.
  const showBoard = station.status.kind === "live" || station.status.kind === "reconnecting";
  return (
    <ModeOutlet
      {...base}
      notice={<KitchenStatus status={station.status} onEnrol={station.enrol} />}
      // ORDR-118: the daily controls, beside the board, enrolled or not.
      panel={panel}
      {...(showBoard && station.routeProps !== undefined ? { routeProps: { kitchen: station.routeProps } } : {})}
    />
  );
}

function CustomerOutlet({ wiring, ...base }: OutletBase & { wiring: CustomerWiring }): ReactElement {
  const ordering = useCustomerOrdering({
    client: wiring.client,
    siteId: wiring.siteId,
    secrets: wiring.secrets,
    currency: wiring.currency,
    ...(wiring.entry === undefined ? {} : { entry: wiring.entry }),
    ...(wiring.brandNodeId === undefined ? {} : { brandNodeId: wiring.brandNodeId }),
    ...(wiring.locationNodeId === undefined ? {} : { locationNodeId: wiring.locationNodeId }),
    ...(wiring.card === undefined ? {} : { cardPlatform: wiring.card.platform }),
  });
  const [placed, setPlaced] = useState<PlacedOrder | null>(null);
  const [commerce] = useState(() => createCommerceClient(wiring.client, wiring.secrets));
  const timeZone = ordering.storefront.hours?.kind === "ok" ? ordering.storefront.hours.hours.timezone : undefined;

  // The card exists only as a way out of a pay-in-store refusal: nothing here
  // runs until `useCheckout` asks for it after commerce's 403.
  const { card } = wiring;
  const cardConfig = ordering.cardConfig;
  const payByCard = useMemo<CardPayment | null>(
    () =>
      card === undefined || cardConfig === null
        ? null
        : {
            render: () => (
              <Suspense fallback={null}>
                <CardFields config={cardConfig} slot={card.slot} {...(card.deps === undefined ? {} : { deps: card.deps })} />
              </Suspense>
            ),
            tokeniser: () => wiring.tokeniser ?? createCardTokeniser(card.slot),
          },
    [card, cardConfig, wiring.tokeniser],
  );

  // Memoised for the same reason as `ModeOutlet`'s: reporting is not idempotent.
  const { onFlowFault } = base;
  const decision = useMemo(
    () => (ordering.flow === undefined ? undefined : gateFlow(ordering.flow, onFlowFault)),
    [ordering.flow, onFlowFault],
  );

  const orderId = placed?.orderId ?? wiring.orderId ?? null;
  if (orderId !== null) {
    return (
      <Suspense fallback={null}>
        <OrderStatusPage
          client={wiring.client}
          commerce={commerce}
          orderId={orderId}
          placed={placed}
          {...(timeZone === undefined ? {} : { timeZone })}
        />
      </Suspense>
    );
  }

  const notice = (
    <>
      {/* A notice, never a replacement: the menu below stays mounted in
          every state, and only placement is refused. */}
      <StorefrontStatus
        storefront={ordering.storefront}
        mode={ordering.storefront.hours?.kind === "ok" ? ordering.storefront.hours.hours.mode : null}
        changeLocationHref={changeLocationHref(wiring)}
      />
      <CustomerStatus status={ordering.status} client={wiring.client} navigate={wiring.navigate ?? documentLoad} />
      {decision?.proceed === false && <FlowRefused />}
      {/* HQ ruling S1: the standing disclosure direction, once per menu
          page, above the menu rather than on every item. */}
      {ordering.status.kind === "ready" && <MenuDisclosure />}
    </>
  );
  // A refused flow, or no services yet, shows its notices and nothing to
  // order from: the client refuses rather than continuing to payment. Always
  // through OrderScreens, so the notices keep one place as the menu arrives.
  const services = decision?.proceed === true ? (ordering.services ?? null) : null;
  return (
    <OrderScreens
      sections={ordering.catalogue}
      services={services}
      header={notice}
      checkout={{ ...ordering.checkout, card: payByCard }}
      onPlaced={(order) => {
        rememberOrder(order.orderId);
        setPlaced(order);
      }}
    />
  );
}

/**
 * The Status page from the order link alone (ORDR-226). No Site means no
 * location hours, so the times are drawn in the device's own zone.
 */
function OrderLinkOutlet({ wiring }: { wiring: OrderLinkWiring }): ReactElement {
  const [commerce] = useState(() => createCommerceClient(wiring.client, wiring.secrets));
  return (
    <Suspense fallback={null}>
      <OrderStatusPage client={wiring.client} commerce={commerce} orderId={wiring.orderId} placed={null} />
    </Suspense>
  );
}

function documentLoad(href: string): void {
  window.location.assign(href);
}

/** The chooser for this brand, keeping `entry`; `null` when nothing names the brand. */
function changeLocationHref(wiring: CustomerWiring): string | null {
  if (wiring.brandNodeId === undefined) {
    return null;
  }
  const query = new URLSearchParams({ brand: wiring.brandNodeId });
  if (wiring.entry !== undefined) {
    query.set("entry", wiring.entry);
  }
  return `/order?${query.toString()}`;
}

function currentPath(): string {
  return typeof window === "undefined" ? ORDERING_BASE_PATH : window.location.pathname;
}

export function OrderingShell({
  nodes,
  surface = "staff",
  ownUserId,
  initialActingNodeId,
  flow,
  onFlowSubmit,
  onFlowFault,
  siteIdByNode = NO_SITES,
  nodeTypeByNode = NO_TYPES,
  kitchen,
  customer,
  client,
  chooser,
  orderLink,
  staffHosts = STAFF_HOSTS,
  businessChosen = true,
  onboardingEnabled = ONBOARDING_ENABLED,
}: OrderingShellProps): ReactElement {
  const [actingNodeId, setActingNodeId] = useState<string>(
    initialActingNodeId ?? nodes[0]?.id ?? "",
  );
  const [path, setPath] = useState<string>(currentPath);
  // Bumped by "Onboard business", so a setup left open gives way to the form.
  const [onboardingVisit, setOnboardingVisit] = useState(0);
  const actionsSlot = useContext(BusinessActionsSlot);

  // Back and forward are the two controls this app does not draw and cannot
  // ignore: a diner who taps back out of the review screen and finds the URL
  // changed but the screen not is looking at a bug, not at a browser.
  useEffect(() => {
    function syncFromHistory(): void {
      setPath(currentPath());
    }
    window.addEventListener("popstate", syncFromHistory);
    return () => window.removeEventListener("popstate", syncFromHistory);
  }, []);

  const actingNode = nodes.find((node) => node.id === actingNodeId) ?? nodes[0];
  const pathMode = modeFromPath(path);
  // ORDR-194. Decided from `node_type` alone, never from a role or a seat.
  const atReseller = actingNode !== undefined && nodeTypeByNode[actingNode.id] === "RESELLER";
  // "Onboard restaurant" is offered when every one of its gates passes: the
  // build switch, a staff host, a client to call with, a RESELLER node and an
  // active business (ORDR-204), and the capability at the node being acted for.
  const onboardingOffered =
    onboardingEnabled &&
    atReseller &&
    businessChosen &&
    surface === "staff" &&
    client !== undefined &&
    actingNode !== undefined &&
    actingNode.capabilities.includes(ONBOARD_CAPABILITY) &&
    typeof window !== "undefined" &&
    isStaffHost(window.location.hostname, staffHosts);
  // At a reseller the node keeps one capability at most, so the pack's nav
  // model offers nothing that needs a brand, location or site. Every node that
  // is not a reseller loses `merchant:onboard` (ORDR-204), whatever it carries.
  const shellNodes = useMemo<readonly ActingNode[]>(
    () =>
      nodes.map((node) => {
        if (atReseller && node.id === actingNode?.id) {
          return { ...node, capabilities: onboardingOffered ? [ONBOARD_CAPABILITY] : [] };
        }
        return nodeTypeByNode[node.id] === "RESELLER" || !node.capabilities.includes(ONBOARD_CAPABILITY)
          ? node
          : { ...node, capabilities: node.capabilities.filter((capability) => capability !== ONBOARD_CAPABILITY) };
      }),
    [atReseller, actingNode, nodes, nodeTypeByNode, onboardingOffered],
  );
  const shellNode = shellNodes.find((node) => node.id === actingNode?.id);
  // Memoised because `ShellProvider` keys its context memo on this identity; a
  // fresh closure every render would rebuild the nav model on every keystroke.
  const has = useMemo(() => capabilityCheckFor(shellNode), [shellNode]);
  // At a reseller, `/` and `/onboarding` open onboarding when it is offered;
  // every other address, a brand mode's included, opens the pointer alone.
  const mode: ShellMode | null = atReseller
    ? onboardingOffered && (pathMode === null || pathMode === "onboarding")
      ? "onboarding"
      : null
    : pathMode;
  // What the configuration surfaces are told about the node being acted for.
  // A rendering input only (ruling B10); the server decides every write.
  const scope = useMemo<ConfigScope | null>(
    () =>
      actingNode === undefined
        ? null
        : {
            nodeId: actingNode.id,
            nodeName: actingNode.name,
            siteId: siteIdByNode[actingNode.id] ?? null,
            capabilities: actingNode.capabilities,
          },
    [actingNode, siteIdByNode],
  );
  const staffPanels = surface === "staff" && client !== undefined && scope !== null;
  const kitchenPanel =
    mode === "kitchen" && staffPanels ? <DailyControls client={client} scope={scope} /> : undefined;
  // ORDR-101 twice over. ConfigSurface is NOT gated by the shell (ruling B10:
  // the surface makes each field group read-only without its capability), and
  // the grants panel keeps exactly the gate it had.
  const configPanel = mode === "admin" && staffPanels ? <ConfigSurface client={client} scope={scope} /> : null;
  const grantsPanel =
    mode === "admin" && ownUserId !== undefined && has(GRANT_CAPABILITY) ? <GrantsPanel ownUserId={ownUserId} /> : null;
  // ORDR-190. Beside the shell like the grants panel, so the mode gate and this
  // one cannot drift apart into a surface nobody can open. ORDR-191: publish and
  // go-live are offered acting at the RESELLER node holding `merchant:onboard`,
  // never read from the seat's `brand:launch` (HQ): the server requires that at
  // the RESTAURANT, which this app does not resolve, and draws its own 403.
  const launchOffered =
    atReseller && actingNode !== undefined && actingNode.capabilities.includes(ONBOARD_CAPABILITY);
  const onboardingPanel =
    onboardingOffered && mode === "onboarding" && staffPanels ? (
      <Suspense fallback={null}>
        <OnboardingPanel key={onboardingVisit} client={client} launchOffered={launchOffered} />
      </Suspense>
    ) : null;

  const openMode = useCallback((next: ShellMode): void => {
    const to = pathForMode(next);
    window.history.pushState(null, "", to);
    setPath(to);
  }, []);

  // ORDR-209. Drawn on `onboardingPanel`'s own gate, beside the switcher.
  const onboardButton =
    onboardingOffered && staffPanels && actionsSlot !== null
      ? createPortal(
          <button
            type="button"
            onClick={() => {
              openMode("onboarding");
              setOnboardingVisit((visit) => visit + 1);
            }}
            className="rounded-[var(--border-radius-md)] border border-[var(--color-neutral-300)] px-3 py-1 text-sm"
          >
            Onboard business
          </button>,
          actionsSlot,
        )
      : null;

  // ORDR-139. The Ordering App and the Organization admin take the
  // organization's brand; the Order Manager (kitchen, staff) NEVER does, and
  // neither does a staff door whose mode the framework has yet to pick.
  const branded = surface === "customer" || mode === "customer" || mode === "admin";

  const outlet: OutletBase = {
    nodes: shellNodes,
    actingNodeId,
    onActingNodeChange: setActingNodeId,
    mode,
    onModeChange: openMode,
    ...(onFlowSubmit === undefined ? {} : { onFlowSubmit }),
    ...(onFlowFault === undefined ? {} : { onFlowFault }),
  };

  const body = (
    <>
      <ModeTitle mode={mode} venue={actingNode?.name} />
      {onboardButton}

      {/* The notice belongs to STAFF surfaces only. A diner holds no node by
          design, and telling them they are "not set up to order" would be a
          lie about a customer, not a fact about a venue. */}
      {surface === "staff" && nodes.length === 0 ? (
        <PwaShell>
          <p role="alert" data-testid="no-nodes" className="p-4 text-sm">
            You are not set up to order at any venue yet.
          </p>
        </PwaShell>
      ) : (
        <PwaShell>
          {atReseller ? (
            <>
              <ResellerPointer />
              {/* ORDR-203. The panel alone, NOT through `ModeOutlet`: the
                  framework's onboarding route is a LOCATION's (ladder tests,
                  a location id, a port), and with no location to hand it, it
                  told a reseller the app was not connected. Gated exactly as
                  before, by `onboardingPanel`. */}
              {onboardingPanel}
            </>
          ) : surface === "customer" && orderLink !== undefined ? (
            <OrderLinkOutlet wiring={orderLink} />
          ) : surface === "customer" && chooser !== undefined && client !== undefined ? (
            <SplashGate client={client} target={{ level: "brand", nodeId: chooser.brandNodeId }}>
              <LocationChooser client={client} brandNodeId={chooser.brandNodeId} onChoose={chooser.onChoose} />
            </SplashGate>
          ) : mode === "kitchen" && surface === "staff" && kitchen !== undefined ? (
            <KitchenOutlet
              {...outlet}
              wiring={kitchen}
              siteId={siteIdByNode[actingNodeId] ?? null}
              {...(kitchenPanel === undefined ? {} : { panel: kitchenPanel })}
            />
          ) : surface === "customer" && customer !== undefined ? (
            customer.locationNodeId === undefined ? (
              <CustomerOutlet {...outlet} wiring={customer} />
            ) : (
              <SplashGate
                client={customer.client}
                target={{ level: "location", nodeId: customer.locationNodeId }}
                {...(customer.brandNodeId === undefined ? {} : { brandNodeId: customer.brandNodeId })}
              >
                <CustomerOutlet {...outlet} wiring={customer} />
              </SplashGate>
            )
          ) : (
            <ModeOutlet
              {...outlet}
              flow={flow}
              // ORDR-101. Gated on the capability the panel NEEDS, at the node
              // being acted for, rather than on the admin mode. The mode gates
              // on `menu:author`, which no location role holds, so gating on it
              // hid this surface from every location-manager, who are precisely
              // the people it exists for. The node's own capability list comes
              // from the ordering context, so this costs no extra call.
              {...(configPanel === null && grantsPanel === null && onboardingPanel === null
                ? kitchenPanel === undefined
                  ? {}
                  : { panel: kitchenPanel }
                : {
                    panel: (
                      <>
                        {configPanel}
                        {grantsPanel}
                        {onboardingPanel}
                      </>
                    ),
                  })}
            />
          )}
        </PwaShell>
      )}
    </>
  );

  return (
    <ShellProvider
      basePath={ORDERING_BASE_PATH}
      packs={ORDERING_PACKS}
      has={has}
      resolveLabel={resolveOrderingLabel}
    >
      {branded ? <BrandedSurface>{body}</BrandedSurface> : body}
    </ShellProvider>
  );
}

/**
 * The tab's name, in the tenant's words, read back OUT of the mounted shell.
 *
 * `index.html` ships `<title>Order</title>`, which is the customer mode's label
 * on every surface in the app: a kitchen display and a menu builder both sat in
 * a tab called "Order". The label comes through `useShell` rather than from a
 * direct import on purpose: it is the app's own `resolveLabel` arriving back
 * through the provider, which is the thing this migration is for.
 *
 * The read is `resolveLabelFor`, which takes the pack id. UIFR-1656 made that
 * argument mandatory so a pack's resolver is never handed another pack's key,
 * and `mode.*` is ours, so we name ourselves.
 *
 * Where no venue is being acted for (a diner), the tab names the organization
 * instead (ORDR-139). `useBrand()` is empty outside a `BrandedSurface`, so the
 * kitchen's tab can never carry a brand.
 */
function ModeTitle({ mode, venue }: { mode: ShellMode | null; venue?: string }): null {
  const { resolveLabelFor } = useShell();
  const { displayName } = useBrand();
  const where = venue ?? displayName;

  useEffect(() => {
    const name =
      mode === null ? ORDERING_DEFAULT_TITLE : resolveLabelFor(ORDERING_PACK.id, `mode.${mode}`);
    document.title = where === undefined ? name : `${name} at ${where}`;
  }, [mode, where, resolveLabelFor]);

  return null;
}
